import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { createSelector } from 'reselect';
import { Spinner } from 'common/spinners';
import _ from 'lodash';
import {
  Button,
  BaseCard,
  BaseCardBody,
  BaseCardHeader,
} from '@unite-us/ui';
import { useToast } from '@unite-us/app-components';
import { hasFeatureRole } from 'common/utils/Roles/hasFeatureRole';
import { fetchServiceCases } from 'actions/Case/Contact/Group';
import { findContactDocuments } from 'actions/Document';
import {
  fetchContactDocuments,
  renameContactDocument,
  removeDocumentFromContactResources,
  uploadAndAttachContactDocuments,
} from 'actions/Document/Contact/Group';
import RenameDocumentDialog from 'common/form/DocumentUploader/components/RenameDocumentDialog';
import UploadAndAttachDocuments from 'common/form/DocumentUploader/components/UploadAndAttachDocuments';
import ContactDocuments from 'src/components/Facesheet/Uploads/components/ContactDocuments';
import './Uploads.scss';

export class FacesheetUploads extends Component {
  constructor(props) {
    super(props);

    this.onInputChange = this.onInputChange.bind(this);
    this.onRenameDocument = this.onRenameDocument.bind(this);
    this.openRenameDialog = this.openRenameDialog.bind(this);
    this.fetchData = this.fetchData.bind(this);
    this.fetchServiceCases = this.fetchServiceCases.bind(this);
    this.removeFromContact = this.removeFromContact.bind(this);
    this.saveUploadedDocuments = this.saveUploadedDocuments.bind(this);

    this.mounted = false;
    this.state = {
      editMode: false,
      documentSearch: props.searchedTerm,
      loading: false,
      currentDocument: {},
    };
  }

  componentDidMount() {
    this.mounted = true;
    this.fetchDocumentsOnly();
  }

  componentDidUpdate(prevProps) {
    const { contact } = this.props;
    const prevContactId = prevProps.contact?.id;
    const currentContactId = contact?.id;

    if (prevContactId !== currentContactId && this.mounted) {
      this.fetchDocumentsOnly();
    }
  }

  componentWillUnmount() {
    this.mounted = false;
  }

  onRenameDocument(doc) {
    const { contact, groupId } = this.props;
    this.props.renameContactDocument({
      contactId: contact?.id,
      documentId: doc.id,
      groupId,
      title: doc.title,
    });
  }

  onInputChange(documentSearch) {
    if (this.mounted) {
      this.setState({ documentSearch });
    }
  }

  fetchDocumentsOnly() {
    const { groupId, contact } = this.props;
    const contactId = contact?.id;

    if (!this.mounted || !contactId) {
      return;
    }

    this.setState({ loading: true });

    if (!this.props.findContactDocuments) {
      return;
    }

    // Only fetch documents to avoid Redux state conflicts that cause unmounting
    this.props.fetchContactDocuments({ groupId, contactId })
      .then(() => {
        if (this.mounted) {
          this.props.findContactDocuments(contactId, this.state.documentSearch);
          this.setState({ loading: false });
        }
      })
      .catch(() => {
        if (this.mounted) {
          this.setState({ loading: false });
        }
      });
  }

  fetchServiceCases() {
    const { groupId, contact } = this.props;
    const contactId = contact?.id;
    const contactCases = _.get(this.props, 'contactCases', []);

    if (_.isEmpty(contactCases) && contactId) {
      return this.props.fetchServiceCases(groupId, contactId);
    }
    return Promise.resolve();
  }

  openRenameDialog(doc) {
    if (this.mounted) {
      this.setState({ editMode: true, currentDocument: doc }, () => {
        this.renameDocumentDialog.form.getWrappedInstance().dialog.openDialog()
          .then(() => {
            if (this.mounted) {
              this.setState({ editMode: false });
            }
          });
      });
    }
  }

  fetchData() {
    const { groupId, contact } = this.props;
    const contactId = contact?.id;

    if (!this.mounted || !contactId) {
      return;
    }

    this.setState({ loading: true });

    if (!this.props.findContactDocuments) {
      return;
    }

    // Fetch documents first, then service cases with delay to prevent Redux conflicts
    const documentsPromise = this.props.fetchContactDocuments({ groupId, contactId });

    const serviceCasesPromise = new Promise((resolve, reject) => {
      setTimeout(() => {
        if (this.mounted) {
          this.fetchServiceCases()
            .then(resolve)
            .catch(reject);
        } else {
          reject(new Error('Component unmounted before service cases fetch'));
        }
      }, 50);
    });

    Promise.all([documentsPromise, serviceCasesPromise])
      .then(() => {
        if (this.mounted) {
          this.props.findContactDocuments(contactId, this.state.documentSearch);
          this.setState({ loading: false });
        }
      })
      .catch(() => {
        if (this.mounted) {
          this.setState({ loading: false });
        }
      });
  }

  removeFromContact(doc = {}) {
    const { contact, addToast } = this.props;
    return this.props.removeDocumentFromContactResources({
      contactId: contact?.id,
      documentId: doc.id,
      addToast,
    });
  }

  saveUploadedDocuments({ uploadedDocuments, privateToOrg, privateToRole }) {
    const { contact, addToast } = this.props;
    const additionalData = [
      {
        key: 'privateToOrg',
        value: privateToOrg,
      },
      {
        key: 'privateToRole',
        value: privateToRole,
      },
    ];
    if (!_.isEmpty(uploadedDocuments) && contact?.id) {
      this.props.uploadAndAttachContactDocuments({
        contactId: contact.id,
        uploadedDocuments,
        additionalData,
      }).then(() => {
        if (this.mounted) {
          this.fetchData();
          addToast({
            message: 'Documents uploaded',
            type: 'success',
          });
        }
      });
    }
  }

  render() {
    const {
      documents,
      styles,
      hasSocialCareNavigationRole,
      hasRestrictedUploadFlag,
      shouldPortalizeDialogs,
      hideUploadButton,
    } = this.props;
    const {
      currentDocument,
      editMode,
      loading,
    } = this.state;

    const WARNING_TEXT = hasRestrictedUploadFlag ?
      [
        'Reminder: Unless you limit document visibility, all providers working with',
        'a client can access documents uploaded here. To upload documents specific to a referral',
        'or case (especially those with PHI), go to that referral/case record.'].join(' ') :
      [
        'Reminder: Documents uploaded to the Upload Tab are accessible',
        'by all providers working with a client. Please only upload documents specific to a',
        'referral or case (especially PHI) on that case/referral record.'].join(' ');

    return (
      <BaseCard className="uploads">
        <BaseCardHeader title="Client Record Uploads">
          {!hideUploadButton && (
            <UploadAndAttachDocuments
              anchorEl={(
                <Button
                  id="upload-document-btn"
                  label="Upload Document"
                  className="uploads__upload-btn"
                  labelStyle={_.get(styles, 'uploadDocButton', {})}
                  primary
                />
              )}
              dropzoneName="contactDocuments"
              onAttachDocuments={this.saveUploadedDocuments}
              warningText={WARNING_TEXT}
              showDocumentVisibility
              hasSocialCareNavigationRole={hasSocialCareNavigationRole}
            />
          )}
        </BaseCardHeader>
        <BaseCardBody withPadding>
          {
            loading ? <Spinner /> : (
              <div className="uploads__body">
                <ContactDocuments
                  documents={documents.data || []}
                  onRemove={this.removeFromContact}
                  onRename={this.openRenameDialog}
                  shouldPortalizeDialogs={shouldPortalizeDialogs}
                />

                <RenameDocumentDialog
                  doc={currentDocument}
                  ref={(c) => { this.renameDocumentDialog = c; }}
                  onRenameDocument={this.onRenameDocument}
                  dialogOpen={editMode}
                  shouldPortalizeDialogs={shouldPortalizeDialogs}
                />
              </div>
            )
          }
        </BaseCardBody>
      </BaseCard>
    );
  }
}

FacesheetUploads.propTypes = {
  contact: PropTypes.shape({
    id: PropTypes.string.isRequired,
  }).isRequired,
  documents: PropTypes.shape({
    data: PropTypes.array,
  }),
  fetchContactDocuments: PropTypes.func.isRequired,
  fetchServiceCases: PropTypes.func.isRequired,
  findContactDocuments: PropTypes.func.isRequired,
  groupId: PropTypes.string.isRequired,
  params: PropTypes.shape({
    id: PropTypes.string.isRequired,
  }).isRequired,
  removeDocumentFromContactResources: PropTypes.func.isRequired,
  renameContactDocument: PropTypes.func.isRequired,
  searchedTerm: PropTypes.string.isRequired,
  styles: PropTypes.object,
  uploadAndAttachContactDocuments: PropTypes.func.isRequired,
  hasSocialCareNavigationRole: PropTypes.bool,
  hasRestrictedUploadFlag: PropTypes.bool,
  shouldPortalizeDialogs: PropTypes.bool,
  hideUploadButton: PropTypes.bool,
  addToast: PropTypes.func.isRequired,
};

FacesheetUploads.defaultProps = {
  documents: {
    data: [],
  },
  styles: {
    uploadDocButton: {
      textTransform: 'none',
    },
  },
  hasSocialCareNavigationRole: false,
  hasRestrictedUploadFlag: false,
  shouldPortalizeDialogs: false,
  hideUploadButton: false,
};

/**
 * @param { object } - contactDocuments - data from the contact Documents
 * @param { object } - OwnProps - Props receieved from the parent (used to get the contactId)
 * @return { object } - document object with its array of documents && paging object respectively
 */
const generateDocuments = ({ contactDocuments, contactId, ownProps }) => {
  if (_.isEmpty(contactDocuments)) {
    return {};
  }
  if (contactDocuments[contactId] && contactDocuments[contactId].searchedWord) {
    const filteredDocumentsObject = _.merge({}, contactDocuments);
    const documentSearchRegex = new RegExp(contactDocuments[contactId].searchedWord, 'gi');
    const filteredDocuments = contactDocuments[contactId].data.filter((doc) => (doc.title.match(documentSearchRegex)));
    filteredDocumentsObject[contactId].data = filteredDocuments;
    return filteredDocuments.length > 0 ? filteredDocumentsObject[contactId] : contactDocuments[contactId];
  }
  return contactDocuments[ownProps.params.id];
};

// Reselect selectors for memoization
const getContactDocuments = (state) => state.contactDocuments;
const getContactId = (state, ownProps) => ownProps.contactId || _.get(ownProps, 'params.id');
const getOwnProps = (state, ownProps) => ownProps;
const getContacts = (state) => state.contacts.contacts;
const getServiceCases = (state) => state.serviceCase;
const getEmployeeRoles = (state) => _.get(state, 'globalState.currentEmployee.roles', []);

const getContact = createSelector(
  [getContacts, getContactId, getOwnProps],
  (contacts, contactId, ownProps) => _.find(contacts, { id: contactId }) || ownProps.contact,
);

const getContactCases = createSelector(
  [getServiceCases, getContactId],
  (serviceCases, contactId) => {
    const contactCaseObj = _.get(serviceCases, contactId);
    return _.get(contactCaseObj, 'data', []);
  },
);

const getHasSocialCareNavigationRole = createSelector(
  [getEmployeeRoles],
  (employeeRoles) => hasFeatureRole({
    employeeRoles,
    targetRoleKey: 'social_care_navigation',
  }),
);

const getFilteredDocuments = createSelector(
  [getContactDocuments, getContactId, getOwnProps],
  (contactDocuments, contactId, ownProps) => generateDocuments({
    contactDocuments,
    contactId,
    ownProps,
  }),
);

function mapStateToProps(state, ownProps) {
  const contact = getContact(state, ownProps);
  const filteredDocuments = getFilteredDocuments(state, ownProps);
  const contactCases = getContactCases(state, ownProps);
  const hasSocialCareNavigationRole = getHasSocialCareNavigationRole(state, ownProps);

  return {
    groupId: state.session.groupId,
    documents: filteredDocuments,
    contact,
    searchedWord: state.contactDocuments.searchedWord,
    searchedTerm: _.get(ownProps, 'location.query.s', ''),
    contactCases,
    hasSocialCareNavigationRole,
  };
}

const ConnectedFacesheetUploads = connect(mapStateToProps, {
  fetchContactDocuments,
  fetchServiceCases,
  findContactDocuments,
  removeDocumentFromContactResources,
  renameContactDocument,
  uploadAndAttachContactDocuments,
})(FacesheetUploads);

// Wrapper component that provides useToast hook to the class component
function FacesheetUploadsWithToast(props) {
  const { addToast } = useToast();

  return <ConnectedFacesheetUploads {...props} addToast={addToast} />;
}

export default FacesheetUploadsWithToast;
